import Config from '$root/Config';
import store from '$stores/MainStore';
import * as Sentry from '@sentry/vue';

// imports
import EventDispatcher from '$dependencies/EventDispatcher';

// private static properties
const axios = require('axios');

const WRITE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

let authTokenRequest;

function getStoredRefreshToken() {
	const key = Config.storageKeys.refreshToken;
	const raw = localStorage.getItem(key) || sessionStorage.getItem(key);

	if (!raw) {
		return null;
	}

	try {
		return JSON.parse(raw);
	} catch (e) {
		return null;
	}
}

function forceLogout() {
	store.dispatch('session/logOut');
	if (window.location.pathname !== '/') {
		window.location.href = '/';
	}
}

function refreshToken() {
	const token = getStoredRefreshToken();

	if (!token || !token.refresh_token) {
		// Interceptor should handle this first; keep a quiet reject as safety net
		return Promise.reject();
	}

	const params = new URLSearchParams();
	params.append('grant_type', 'refresh_token');
	params.append('refresh_token', token.refresh_token);
	params.append('client_id', Config.app_client_id);
	params.append('client_secret', Config.app_client_secret);

	return axios.post(`${Config.baseUrl}oauth/token`, params);
}

function getAuthToken() {
	if (!authTokenRequest) {
		authTokenRequest = refreshToken()
			.then((response) => {
				resetAuthTokenRequest();
				store.dispatch('session/updateToken', response.data.access_token);
				store.dispatch('session/updateRefreshToken', response.data.refresh_token);
				store.dispatch('session/loadUser');
				return response;
			})
			.catch((err) => {
				resetAuthTokenRequest();
				throw err;
			});
	}

	return authTokenRequest;
}

function resetAuthTokenRequest() {
	authTokenRequest = null;
}

axios.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error.response?.status !== 401) {
			addApiFailureBreadcrumb(error);
			return Promise.reject(error);
		}

		if (error.response.config.url.includes('oauth/token')) {
			forceLogout();
			return Promise.reject(error);
		}

		const storedRefresh = getStoredRefreshToken();
		if (!storedRefresh || !storedRefresh.refresh_token) {
			// Expired/missing session with no refresh token — send user to login quietly
			forceLogout();
			return Promise.reject(error);
		}

		return getAuthToken()
			.then((response) => {
				error.response.config.headers.Authorization = `Bearer ${response.data.access_token}`;
				return axios(error.response.config);
			})
			.catch(() => {
				forceLogout();
				return Promise.reject(error);
			});
	},
);

function addApiFailureBreadcrumb(error) {
	const config = error?.config || error?.response?.config || {};
	const method = (config.method || '').toLowerCase();
	const url = config.url || '';
	const status = error?.response?.status;

	Sentry.addBreadcrumb({
		category: 'api',
		level: 'error',
		message: `${(method || 'request').toUpperCase()} ${url}${status ? ` → ${status}` : ''}`,
		data: {
			method: method || undefined,
			url: url || undefined,
			status,
			isWrite: WRITE_METHODS.has(method),
		},
	});
}

// class definition
export default class Request extends EventDispatcher {
	constructor(method, config) {
		super(config);

		// set properties
		this._config = Object.assign({}, config || {});

		// set method in config
		this._config.method = method;
	}

	run() {
		return axios
			.request(this._config)
			.then((response) => {
				this.emit('success', response);
				return response;
			})
			.catch((error) => {
				throw error;
			});
	}

	destroy() {
		this._config = {};
	}

	// getters & setters
	get data() {
		return this._config.data;
	}

	set data(value) {
		Object.assign(this._config.data, value || {});
	}

	get url() {
		return this._config.url;
	}

	set url(value) {
		this._config.url = value;
	}

	get headers() {
		return this._config.headers;
	}

	set headers(value) {
		Object.assign(this._config.headers, value || {});
	}

	get params() {
		return this._config.params;
	}

	set params(value) {
		Object.assign(this._config.params, value || {});
	}
}
